import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { logAudit } from '../../../../lib/audit';
import type { AdminRecord } from '../types';
import { roleLabel } from '../types';

interface ModuleDef {
  key: string;
  label: string;
  icon: string;
  section: string;
}

const MODULES: ModuleDef[] = [
  { key: 'dashboard',       label: 'Dashboard',       icon: 'ri-dashboard-3-line',       section: 'Principal'  },
  { key: 'usuarios',        label: 'Usuários',         icon: 'ri-group-line',              section: 'Principal'  },
  { key: 'planos',          label: 'Planos',           icon: 'ri-price-tag-3-line',        section: 'Principal'  },
  { key: 'assinaturas',     label: 'Assinaturas',      icon: 'ri-file-list-3-line',        section: 'Principal'  },
  { key: 'pagamentos',      label: 'Pagamentos',       icon: 'ri-bank-card-line',          section: 'Principal'  },
  { key: 'feedbacks',       label: 'Feedbacks',        icon: 'ri-feedback-line',           section: 'Principal'  },
  { key: 'sugestoes',       label: 'Sugestões',        icon: 'ri-lightbulb-flash-line',    section: 'Principal'  },
  { key: 'comunicados',     label: 'Comunicados',      icon: 'ri-megaphone-line',          section: 'Principal'  },
  { key: 'gateways',        label: 'Gateways',         icon: 'ri-secure-payment-line',     section: 'Sistema'    },
  { key: 'integracoes',     label: 'Integrações',      icon: 'ri-plug-2-line',             section: 'Sistema'    },
  { key: 'webhooks',        label: 'Webhooks',         icon: 'ri-webhook-line',            section: 'Sistema'    },
  { key: 'administradores', label: 'Administradores',  icon: 'ri-shield-user-line',        section: 'Sistema'    },
  { key: 'configuracoes',   label: 'Configurações',    icon: 'ri-settings-3-line',         section: 'Sistema'    },
  { key: 'auditoria',       label: 'Auditoria',        icon: 'ri-shield-check-line',       section: 'Sistema'    },
];

/** Permissões padrão por papel (espelhando useAdminRole) */
const ROLE_DEFAULTS: Record<string, string[]> = {
  dashboard:        ['master_admin', 'admin', 'support', 'finance'],
  usuarios:         ['master_admin', 'admin', 'support'],
  planos:           ['master_admin', 'admin'],
  assinaturas:      ['master_admin', 'admin', 'support', 'finance'],
  pagamentos:       ['master_admin', 'admin', 'finance'],
  feedbacks:        ['master_admin', 'admin', 'support'],
  sugestoes:        ['master_admin', 'admin'],
  comunicados:      ['master_admin', 'admin'],
  gateways:         ['master_admin', 'finance'],
  integracoes:      ['master_admin'],
  webhooks:         ['master_admin', 'admin'],
  administradores:  ['master_admin'],
  configuracoes:    ['master_admin'],
  auditoria:        ['master_admin', 'admin'],
};

const sections = ['Principal', 'Sistema'];

interface Props {
  admin: AdminRecord;
  isSelf: boolean;
  onUpdate: () => void;
}

export default function AdminModulePermissions({ admin, isSelf, onUpdate }: Props) {
  const [perms, setPerms] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [dirty, setDirty] = useState(false);

  // Inicializa: se há module_permissions salvo, usa. Senão, usa os defaults do papel.
  useEffect(() => {
    const initial: Record<string, boolean> = {};
    for (const m of MODULES) {
      if (admin.module_permissions !== null && typeof admin.module_permissions[m.key] === 'boolean') {
        initial[m.key] = admin.module_permissions[m.key];
      } else {
        initial[m.key] = (ROLE_DEFAULTS[m.key] ?? []).includes(admin.role);
      }
    }
    setPerms(initial);
    setDirty(false);
  }, [admin.userId, admin.role, admin.module_permissions]);

  const toggle = (key: string) => {
    setPerms((prev) => ({ ...prev, [key]: !prev[key] }));
    setDirty(true);
    setMsg('');
  };

  const resetToRoleDefaults = () => {
    const defaults: Record<string, boolean> = {};
    for (const m of MODULES) {
      defaults[m.key] = (ROLE_DEFAULTS[m.key] ?? []).includes(admin.role);
    }
    setPerms(defaults);
    setDirty(true);
    setMsg('');
  };

  const savePermissions = async () => {
    setSaving(true);
    setMsg('');

    const { error } = await supabase
      .from('user_roles')
      .update({ module_permissions: perms, updated_at: new Date().toISOString() })
      .eq('id', admin.roleId);

    if (!error) {
      await logAudit({
        action: 'update',
        resource: 'admin_module_permissions',
        resource_id: admin.userId,
        details: { module_permissions: perms },
      });
      setMsg('Permissões salvas com sucesso!');
      setDirty(false);
      await onUpdate();
    } else {
      setMsg('Erro ao salvar: ' + error.message);
    }

    setSaving(false);
    setTimeout(() => setMsg(''), 4000);
  };

  const enabledCount = Object.values(perms).filter(Boolean).length;
  const isMasterAdmin = admin.role === 'master_admin';

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center bg-cyan-600/20 rounded-xl">
            <i className="ri-layout-grid-line text-cyan-400 text-[15px]" />
          </div>
          <h3 className="text-[15px] font-600 text-white">Permissões por Módulo</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-white/30 bg-white/5 px-2.5 py-1 rounded-full">
            {enabledCount}/{MODULES.length} módulos ativos
          </span>
        </div>
      </div>

      {isMasterAdmin ? (
        <div className="mt-4 p-4 bg-violet-600/10 border border-violet-500/20 rounded-xl">
          <div className="flex items-center gap-2 text-violet-300">
            <i className="ri-shield-star-line text-base" />
            <p className="text-[13px] font-600">Master Admin tem acesso total</p>
          </div>
          <p className="text-[12px] text-violet-300/60 mt-1">
            O papel <strong>Master Admin</strong> possui acesso irrestrito a todos os módulos por definição. 
            Permissões por módulo não se aplicam a este papel.
          </p>
        </div>
      ) : (
        <>
          <p className="text-[12px] text-white/40 mt-1 mb-5">
            Controla quais módulos este administrador ({roleLabel[admin.role]}) pode visualizar e acessar.
            Sobrescreve as permissões padrão do papel.
          </p>

          {isSelf ? (
            <div className="p-4 bg-orange-600/10 border border-orange-500/20 rounded-xl">
              <p className="text-[13px] text-orange-300 font-600">
                <i className="ri-error-warning-line mr-1.5" />Você não pode editar suas próprias permissões.
              </p>
            </div>
          ) : (
            <>
              {sections.map((section) => {
                const items = MODULES.filter((m) => m.section === section);
                return (
                  <div key={section} className="mb-5">
                    <p className="text-[10px] font-700 text-white/30 uppercase tracking-wider mb-3 px-1">{section}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {items.map((mod) => {
                        const isOn = perms[mod.key] ?? false;
                        const roleDefault = (ROLE_DEFAULTS[mod.key] ?? []).includes(admin.role);
                        const isOverride = admin.module_permissions !== null &&
                          typeof admin.module_permissions[mod.key] === 'boolean' &&
                          admin.module_permissions[mod.key] !== roleDefault;

                        return (
                          <button
                            key={mod.key}
                            onClick={() => toggle(mod.key)}
                            className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer text-left ${
                              isOn
                                ? 'bg-emerald-600/10 border-emerald-500/30'
                                : 'bg-white/3 border-white/8 opacity-60'
                            }`}
                          >
                            {/* Toggle visual */}
                            <div className={`w-9 h-5 rounded-full relative transition-colors shrink-0 overflow-hidden ${
                              isOn ? 'bg-emerald-500' : 'bg-white/15'
                            }`}>
                              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                                isOn ? 'left-4' : 'left-0.5'
                              }`} />
                            </div>

                            <div className="flex-1 overflow-hidden">
                              <div className="flex items-center gap-1.5">
                                <i className={`${mod.icon} text-[13px] ${isOn ? 'text-emerald-400' : 'text-white/30'}`} />
                                <span className={`text-[12px] font-600 truncate ${isOn ? 'text-white' : 'text-white/40'}`}>
                                  {mod.label}
                                </span>
                              </div>
                              {isOverride && (
                                <span className="text-[10px] text-amber-400/70">
                                  <i className="ri-edit-line mr-0.5" />customizado
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-white/8">
                <button
                  onClick={resetToRoleDefaults}
                  className="text-[12px] text-white/40 hover:text-white/70 transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-refresh-line" />
                  Restaurar padrões do papel
                </button>

                <div className="flex items-center gap-3">
                  {msg && (
                    <span className={`text-[12px] ${msg.includes('Erro') ? 'text-red-400' : 'text-emerald-400'}`}>
                      {msg}
                    </span>
                  )}
                  {dirty && (
                    <button
                      onClick={savePermissions}
                      disabled={saving}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-[12px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                    >
                      {saving ? (
                        <><i className="ri-loader-4-line animate-spin mr-1.5" />Salvando...</>
                      ) : (
                        <><i className="ri-save-line mr-1.5" />Salvar permissões</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
